import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Button, message, Spin, Layout, Select, Space, Tooltip, Avatar } from 'antd';
import { ReloadOutlined, ClockCircleOutlined, UserOutlined, LogoutOutlined, AlertOutlined, EditOutlined, DollarOutlined,   NodeIndexOutlined,  FileTextOutlined,  ImportOutlined,  ExportOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { CircleDollarSign, Microscope ,Star, MessageSquareText  } from 'lucide-react';
import TabNavigation from './TabNavigation';
import ObservatoryService from '../services/ObservatoryService';
import BarChartComponent from './BarChartComponent';
import PieChartComponent from './PieChartComponent';
import LatestFeedbackCard from './LatestFeedbackCard';
import AlertRulesModal from './AlertRulesModal';
import CurrentAlertsCard from './CurrentAlertsCard';
import SummaryMetricsCard from './SummaryMetricsCard';
import EnvVersionLabel from './Envversionlabel';


const { Title, Text } = Typography;
const { Header, Sider, Content } = Layout;
const { Option } = Select;

/**
 * AgentObservatoryHome - The observability dashboard showing agent metrics
 * 
 * @param {Object} props
 * @param {string} props.username - Current user's username
 * @param {string} props.sessionId - Current session ID
 * @param {string} props.userRole - Current user's role
 * @param {Array} props.userFeatures - User's available features/tabs
 * @param {Array} props.tabOrder - Order of tabs to display
 * @param {function} props.onLogout - Logout handler function
 */
const AgentObservatoryHome = ({ username, sessionId, userRole, userFeatures, tabOrder, onLogout }) => {
  
  const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
  const navigate = useNavigate();
  
  // State for features
  const [features, setFeatures] = useState(userFeatures || ['HUB', 'OBSERVATORY']);
  const [localTabOrder, setLocalTabOrder] = useState(tabOrder || ['HUB', 'RCM', 'COLAB', 'MCP', 'OBSERVATORY']);
  
  // Filter states - Load from localStorage if available
  const [timePeriod, setTimePeriod] = useState(() => {
    const saved = localStorage.getItem('observatory_timePeriod');
    return saved || '7d';
  });
  const [agentNames, setAgentNames] = useState(() => {
    const saved = localStorage.getItem('observatory_agentNames');
    return saved ? JSON.parse(saved) : [];
  });
  const [agentTypes, setAgentTypes] = useState(() => {
    const saved = localStorage.getItem('observatory_agentTypes');
    return saved ? JSON.parse(saved) : [];
  });
  const [executionContexts, setExecutionContexts] = useState(() => {
    const saved = localStorage.getItem('observatory_executionContexts');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Available filter options
  const [availableAgentNames, setAvailableAgentNames] = useState([]);
  const [availableAgentTypes, setAvailableAgentTypes] = useState([]);
  const [availableExecutionContexts, setAvailableExecutionContexts] = useState([]);
  
  // Chart data states
  const [chart1Data, setChart1Data] = useState(null);
  const [chart2Data, setChart2Data] = useState(null);
  const [chart3Data, setChart3Data] = useState(null);
  const [chart4Data, setChart4Data] = useState(null);
  const [chart5Data, setChart5Data] = useState(null);
  const [chart6Data, setChart6Data] = useState(null);
  const [chart7Data, setChart7Data] = useState(null);

  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  
  
  // Loading states per chart
  const [loadingStates, setLoadingStates] = useState({
    chart1: false,
    chart2: false,
    chart3: false,
    chart4: false,
    chart5: false,
    chart6: false,
    chart7: false,
  });
  
  // Last computation timestamp
  const [lastComputation, setLastComputation] = useState(null);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [computing, setComputing] = useState(false);
  
  // Alert modal states
  const [alertRulesModalVisible, setAlertRulesModalVisible] = useState(false);
  const [currentAlertsModalVisible, setCurrentAlertsModalVisible] = useState(false);
  
  // Time period options
  const timePeriodOptions = [
    { value: '24h', label: 'Last 24 Hours' },
    { value: '48h', label: 'Last 48 Hours' },
    { value: '72h', label: 'Last 72 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '15d', label: 'Last 15 Days' },
    { value: '30d', label: 'Last 30 Days' },
  ];

  // Alert state
  const [alertCount, setAlertCount] = useState(0);

  // Load filter options on mount and when time period changes
  useEffect(() => {
    loadFilterOptions();
    loadLastComputation();
  }, []);

  // Reload filter options when time period changes
  useEffect(() => {
    loadFilterOptions();
  }, [timePeriod]);
  
  // Auto-load charts when filter options are loaded (on mount and when filters change)
  useEffect(() => {
    // Only auto-load if we have filter options loaded and charts are empty
    if (!loadingFilters && availableAgentNames.length > 0 && !chart1Data) {
      console.log('Auto-loading charts with default filters...');
      handleApplyFilters();
    }
  }, [loadingFilters, availableAgentNames]);
  
  // Save filter selections to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('observatory_timePeriod', timePeriod);
  }, [timePeriod]);
  
  useEffect(() => {
    localStorage.setItem('observatory_agentNames', JSON.stringify(agentNames));
  }, [agentNames]);
  
  useEffect(() => {
    localStorage.setItem('observatory_agentTypes', JSON.stringify(agentTypes));
  }, [agentTypes]);
  
  useEffect(() => {
    localStorage.setItem('observatory_executionContexts', JSON.stringify(executionContexts));
  }, [executionContexts]);
  
  // Refresh features on navigation
  useEffect(() => {
    const refreshFeatures = async () => {
      try {
        const response = await fetch(`${baseUrl}/features`, {
          headers: { 'Authorization': `Bearer ${sessionId}` },
          credentials: 'include'
        });
        
        if (response.ok) {
          const { features: serverFeatures, tab_order } = await response.json();
          setFeatures(serverFeatures);
          if (tab_order) {
            setLocalTabOrder(tab_order);
          }
        }
      } catch (error) {
        console.warn('Failed to refresh features, using fallback:', error);
      }
    };
    
    if (sessionId && !userFeatures) {
      refreshFeatures();
    }
  }, [sessionId, userFeatures]);
  
  // Load filter options from backend based on selected time period
  const loadFilterOptions = async () => {
    try {
      setLoadingFilters(true);
      const filterData = await ObservatoryService.getFilterOptions(timePeriod);
      
      setAvailableAgentNames(filterData.agent_names || []);
      setAvailableAgentTypes(filterData.agent_types || []);
      setAvailableExecutionContexts(filterData.execution_contexts || []);
      
      // Only reset selections if they're empty (first load) or if saved selections don't exist
      // Otherwise, keep the user's saved selections
      const hasAgentNames = agentNames.length > 0;
      const hasAgentTypes = agentTypes.length > 0;
      const hasExecutionContexts = executionContexts.length > 0;
      
      // If no saved selections, default to "All"
      if (!hasAgentNames) {
        setAgentNames(filterData.agent_names || []);
      }
      if (!hasAgentTypes) {
        setAgentTypes(filterData.agent_types || []);
      }
      if (!hasExecutionContexts) {
        setExecutionContexts(filterData.execution_contexts || []);
      }
      
    } catch (error) {
      console.error('Failed to load filter options:', error);
      message.error('Failed to load filter options');
    } finally {
      setLoadingFilters(false);
    }
  };
  
  // Load last computation timestamp
  const loadLastComputation = async () => {
    try {
      const computationData = await ObservatoryService.getLastComputation();
      setLastComputation(computationData);
    } catch (error) {
      console.error('Failed to load last computation:', error);
    }
  };

  const handleAlertsChanged = async () => {
    try {
        const data = await ObservatoryService.getCurrentAlerts();
        setAlertCount(data.alerts?.length || 0);
    } catch (error) {
        console.error('Error refreshing alert count:', error);
    }
  };


  // Format large numbers with K/M suffixes
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(2) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  };

  // Format currency
  const formatCurrency = (num) => {
    if (num === null || num === undefined) return '-';
    if (num >= 1000) {
      return '$' + (num / 1000).toFixed(2) + 'K';
    }
    if (num >= 1) {
      return '$' + num.toFixed(2);
    }
    return '$' + num.toFixed(4);
  };

  // Format small currency (for per-trace costs)
  const formatSmallCurrency = (num) => {
    if (num === null || num === undefined) return '-';
    if (num >= 0.01) {
      return '$' + num.toFixed(4);
    }
    return '$' + num.toFixed(6);
  };



  // Build metrics configuration for SummaryMetricsCard
  const buildSummaryMetrics = (data) => {
    if (!data) return [];
    
    return [
      {
        key: 'total_cost',
        title: 'Total Cost',
        value: data.total_cost,
        formatter: formatCurrency,
        icon: <DollarOutlined />,
        color: '#162349',
      },
      {
        key: 'total_traces',
        title: 'Total Traces',
        value: data.total_traces,
        formatter: formatNumber,
        icon: <NodeIndexOutlined />,
        color: '#349305',
      },
      {
        key: 'input_tokens',
        title: 'Input Tokens',
        value: data.total_input_tokens,
        formatter: formatNumber,
        icon: <ImportOutlined />,
        color: '#0765bc',
      },
      {
        key: 'output_tokens',
        title: 'Output Tokens',
        value: data.total_output_tokens,
        formatter: formatNumber,
        icon: <ExportOutlined />,
        color: '#162349',
      },
      {
        key: 'avg_cost_per_trace',
        title: 'Avg Cost/Trace',
        value: data.avg_input_cost_per_trace + data.avg_output_cost_per_trace,
        formatter: formatSmallCurrency,
        icon: <DollarOutlined />,
        color: '#ad5d07',
      },
      {
        key: 'avg_tokens_per_trace',
        title: 'Avg Tokens/Trace',
        value: data.avg_tokens_per_trace,
        formatter: (v) => formatNumber(Math.round(v)),
        icon: <FileTextOutlined />,
        color: '#09a9a9',
      },
    ];
  };





  // Handle compute metrics button
const handleComputeMetrics = async () => {
  try {
    setComputing(true);
    message.loading({ content: 'Computing metrics for all time periods...', key: 'compute', duration: 0 });
    
    const result = await ObservatoryService.computeMetrics();
    await loadLastComputation();
    
    // Refresh alert count after computing metrics
    try {
      const alertData = await ObservatoryService.getCurrentAlerts();
      setAlertCount(alertData.alerts?.length || 0);
      
      // Show info about alerts triggered
      if (result.alerts_triggered > 0) {
        message.warning({ 
          content: `Metrics computed! ${result.alerts_triggered} alert(s) triggered.`, 
          key: 'compute', 
          duration: 5 
        });
      } else {
        message.success({ 
          content: 'Metrics computed successfully! No alerts triggered.', 
          key: 'compute', 
          duration: 3 
        });
      }
    } catch (error) {
      console.error('Error refreshing alert count:', error);
      message.success({ content: 'Metrics computed successfully!', key: 'compute', duration: 3 });
    }
    
    // Reload charts with current filters
    handleApplyFilters();
    
  } catch (error) {
    console.error('Failed to compute metrics:', error);
    message.error({ content: `Failed to compute metrics: ${error.message}`, key: 'compute', duration: 5 });
  } finally {
    setComputing(false);
  }
};

  
    // Load alert count on mount and refresh periodically
    useEffect(() => {
    const loadAlertCount = async () => {
        try {
        const data = await ObservatoryService.getCurrentAlerts();
        setAlertCount(data.alerts?.length || 0);
        } catch (error) {
        console.error('Error loading alert count:', error);
        }
    };
    
    loadAlertCount();
    
    // Optional: Auto-refresh every 30 seconds
    const interval = setInterval(loadAlertCount, 30000);
    return () => clearInterval(interval);
    }, []);



  // Handle apply filters button
  const handleApplyFilters = async () => {
    const filters = {
      timePeriod,
      agentNames: agentNames.length === availableAgentNames.length ? [] : agentNames,
      agentTypes: agentTypes.length === availableAgentTypes.length ? [] : agentTypes,
      executionContexts: executionContexts.length === availableExecutionContexts.length ? [] : executionContexts,
    };
    
    // Load all charts in parallel
    loadChart('chart1', filters, setChart1Data, (f) => ObservatoryService.getTokensInOutData(f));
    loadChart('chart2', filters, setChart2Data, (f) => ObservatoryService.getTraceCountData(f));
    loadChart('chart3', filters, setChart3Data, (f) => ObservatoryService.getLatencyData(f));
    loadChart('chart4', filters, setChart4Data, (f) => ObservatoryService.getCostBarData(f));
    loadChart('chart5', filters, setChart5Data, (f) => ObservatoryService.getCostPieData(f));
    loadChart('chart6', filters, setChart6Data, (f) => ObservatoryService.getCostPerTraceData(f));
    loadChart('chart7', filters, setChart7Data, (f) => ObservatoryService.getTokenPerTraceData(f));

     // Load summary metrics
    loadSummaryMetrics(filters);

  };
  
  // Generic chart loading function
  const loadChart = async (chartId, filters, setDataFunc, serviceFunc) => {
    try {
      setLoadingStates(prev => ({ ...prev, [chartId]: true }));
      const data = await serviceFunc(filters);
      setDataFunc(data);
    } catch (error) {
      console.error(`Failed to load ${chartId}:`, error);
      setDataFunc({ error: error.message });
    } finally {
      setLoadingStates(prev => ({ ...prev, [chartId]: false }));
    }
  };

  
  // Load summary metrics
  const loadSummaryMetrics = async (filters) => {
    try {
      setSummaryLoading(true);
      setSummaryError(null);
      const data = await ObservatoryService.getSummaryMetrics(filters);
      setSummaryData(data);
    } catch (error) {
      console.error('Failed to load summary metrics:', error);
      setSummaryError(error.message);
      setSummaryData(null);
    } finally {
      setSummaryLoading(false);
    }
  };



  // Format last computation timestamp
  const formatLastComputation = () => {
    if (!lastComputation || !lastComputation.computed_at) {
      return 'Never computed';
    }
    
    const computedDate = new Date(lastComputation.computed_at);
    const now = new Date();
    const diffMs = now - computedDate;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    
    return computedDate.toLocaleString();
  };
  
  // Render chart card wrapper
  const renderChartCard = (title, chartComponent, loading, error) => {
    return (
      <Card
        title={<span style={{ fontSize: '16px', fontWeight: '600' }}>{title}</span>}
        style={{
          minHeight: '500px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        }}
        bodyStyle={{ padding: '16px' }}
      >
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
            <Spin size="large" tip={`Loading ${title}...`} />
          </div>
        ) : error ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#f5222d' }}>
            <div style={{ fontSize: '16px', marginBottom: '8px' }}>Error Loading {title}</div>
            <div style={{ fontSize: '14px' }}>{error}</div>
            <div style={{ marginTop: '12px', fontSize: '12px', color: '#999' }}>
              Please adjust filters and try again
            </div>
          </div>
        ) : chartComponent ? (
          chartComponent
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', color: '#999' }}>
            <div>
              <div style={{ fontSize: '16px', marginBottom: '8px' }}>No data available</div>
              <div style={{ fontSize: '14px' }}>Click "Apply Filters" to load data</div>
            </div>
          </div>
        )}
      </Card>
    );
  };
  
  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Left Sidebar - Same as AgentColabHome */}
      <Sider 
        width={60} 
        collapsible 
        collapsed={true}
        collapsedWidth={60}
        trigger={null}
        className="sidebar-gradient"
        style={{ 
          display: 'flex', 
          background: 'linear-gradient(180deg, #162349 0%, #1c97d4 100%)', 
          flexDirection: 'column',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 10
        }}
      >
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'space-between', 
          height: '100%',
          alignItems: 'center',
          padding: '20px 0'
        }}>
          {/* Top icons - empty for now */}
          <div></div>
          
          {/* Bottom icons - User controls */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>

            
            <div id="containerDiv">
              <hr style={{ backgroundColor: 'white', color: "white", height: '10px', border: 'none' }} />
            </div>


            <Tooltip title={username} placement="right">
              <Avatar 
                style={{ 
                  backgroundColor: 'white', 
                  color: '#162349', 
                  cursor: 'pointer',
                  border: '2px solid white'
                }}
                icon={<UserOutlined />} 
              />
            </Tooltip>
            
            <Tooltip title="Logout" placement="right">
              <Button 
                type="text" 
                icon={<LogoutOutlined style={{ color: 'white' }} />} 
                onClick={onLogout}
                style={{ color: 'white' }}
              />
            </Tooltip>
            <EnvVersionLabel style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: "Bold" }} />
          </div>
          
        </div>
      </Sider>
      
      <Layout style={{ marginLeft: '60px' }}>
        {/* Top Header - EXACT SAME STRUCTURE AS AgentColabHome */}
        <Header style={{ 
          background: '#162349', 
          padding: '8px 24px 0 24px',
          height: 'auto',
          color: 'white'
        }}>
          {/* Onaxis Brand and Tab Navigation */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0' }}>
            <TabNavigation 
              features={features}
              activeTab="OBSERVATORY"
              tabOrder={localTabOrder}
              onRefresh={handleComputeMetrics}
              refreshing={computing}
            />
          </div>
        </Header>

        {/* Main Content - EXACT SAME STRUCTURE AS AgentColabHome */}
        <Content style={{ 
          padding: '24px',
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)'
        }}>
          
          {/* Filters Section */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }} align="middle">
            {/* Filter Controls Card */}
            <Col xs={24} lg={20}>
              <Card 
                style={{ 
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                }}
                bodyStyle={{ padding: '16px' }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={6}>
                      <div>
                        <Text strong style={{ display: 'block', marginBottom: '4px' }}>Time Period</Text>
                        <Select
                          value={timePeriod}
                          onChange={setTimePeriod}
                          style={{ width: '100%' }}
                          disabled={loadingFilters}
                        >
                          {timePeriodOptions.map(opt => (
                            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                          ))}
                        </Select>
                      </div>
                    </Col>
                    
                    <Col xs={24} sm={12} md={6}>
                      <div>
                        <Text strong style={{ display: 'block', marginBottom: '4px' }}>Agent Name</Text>
                        <Select
                          mode="multiple"
                          value={agentNames}
                          onChange={setAgentNames}
                          style={{ width: '100%' }}
                          placeholder="Select agents"
                          maxTagCount="responsive"
                          disabled={loadingFilters}
                          allowClear
                          onClear={() => setAgentNames(availableAgentNames)}
                        >
                          {availableAgentNames.map(name => (
                            <Option key={name} value={name}>{name}</Option>
                          ))}
                        </Select>
                      </div>
                    </Col>
                    
                    <Col xs={24} sm={12} md={6}>
                      <div>
                        <Text strong style={{ display: 'block', marginBottom: '4px' }}>Agent Type</Text>
                        <Select
                          mode="multiple"
                          value={agentTypes}
                          onChange={setAgentTypes}
                          style={{ width: '100%' }}
                          placeholder="Select types"
                          maxTagCount="responsive"
                          disabled={loadingFilters}
                          allowClear
                          onClear={() => setAgentTypes(availableAgentTypes)}
                        >
                          {availableAgentTypes.map(type => (
                            <Option key={type} value={type}>{type}</Option>
                          ))}
                        </Select>
                      </div>
                    </Col>
                    
                    <Col xs={24} sm={12} md={6}>
                      <div>
                        <Text strong style={{ display: 'block', marginBottom: '4px' }}>Agent Execution Type</Text>
                        <Select
                          mode="multiple"
                          value={executionContexts}
                          onChange={setExecutionContexts}
                          style={{ width: '100%' }}
                          placeholder="Select contexts"
                          maxTagCount="responsive"
                          disabled={loadingFilters}
                          allowClear
                          onClear={() => setExecutionContexts(availableExecutionContexts)}
                        >
                          {availableExecutionContexts.map(ctx => (
                            <Option key={ctx} value={ctx}>{ctx}</Option>
                          ))}
                        </Select>
                      </div>
                    </Col>
                  </Row>
                  
                  <Row justify="flex-start" align="middle">
                    <Col>
                      <Space size="middle">
                        <Button 
                          type="primary" 
                          size="large"
                          onClick={handleApplyFilters}
                          disabled={loadingFilters}
                          style={{
                            background: '#92A372',
                            borderColor: '#92A372',
                          }}
                        >
                          Apply Filters
                        </Button>
                        <Button 
                          type="default" 
                          size="large"
                          icon={<ReloadOutlined />}
                          onClick={handleComputeMetrics}
                          loading={computing}
                          disabled={loadingFilters}
                        >
                          Compute Metrics
                        </Button>
                        <Space size="small">
                          <ClockCircleOutlined style={{ fontSize: '16px', color: '#999' }} />
                          <Text type="secondary">
                            Last computed: {formatLastComputation()}
                          </Text>
                        </Space>
                      </Space>
                    </Col>
                  </Row>
                </Space>
              </Card>
            </Col>
            
            {/* Action Icons - Outside Card */}
            <Col xs={24} lg={4}>
              <div style={{ 
                display: 'flex', 
                gap: '16px',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
              }}>
                <Tooltip title="View Observability Alerts" placement="top">
                <Button
                    shape="circle"
                    size="large"
                    icon={<AlertOutlined style={{ 
                    fontSize: '28px', 
                    color: alertCount > 0 ? 'white' : '#162349'  // White if alerts, purple otherwise
                    }} />}
                    onClick={() => setCurrentAlertsModalVisible(true)}
                    style={{
                    width: '64px',
                    height: '64px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: alertCount > 0 
                        ? '0 4px 12px rgba(245, 34, 45, 0.3)'  // Red shadow if alerts
                        : '0 4px 12px rgba(22, 35, 73, 0.2)',  // Purple shadow otherwise
                    border: alertCount > 0 ? '2px solid #f5222d' : '2px solid #e8e8e8',
                    background: alertCount > 0 ? '#f5222d' : 'white'  // Red bg if alerts, white otherwise
                    }}
                />
                </Tooltip>
                
                <Tooltip title="Edit Observability Alert Rules" placement="top">
                  <Button
                    shape="circle"
                    size="large"
                    icon={<EditOutlined style={{ fontSize: '28px', color: '#162349' }} />}
                    onClick={() => setAlertRulesModalVisible(true)}
                    style={{
                      width: '64px',
                      height: '64px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(22, 35, 73, 0.2)',
                      border: '2px solid #e8e8e8',
                      background: 'white'
                    }}
                  />
                </Tooltip>
                <Tooltip title="LLM Tracing" placement="top">
                  <Button
                    shape="circle"
                    size="large"
                    icon={<MessageSquareText style={{ fontSize: '28px', color: '#162349' }} />}
                    onClick={() => navigate('/observe/llm-tracing')}
                    style={{
                      width: '64px',
                      height: '64px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(22, 35, 73, 0.2)',
                      border: '2px solid #e8e8e8',
                      background: 'white'
                    }}
                  />
                </Tooltip>


              </div>
            </Col>
          </Row>
          
          {/* Summary Metrics Row */}
          <SummaryMetricsCard 
            metrics={buildSummaryMetrics(summaryData)}
            loading={summaryLoading}
            error={summaryError}
            periodLabel={summaryData?.metadata ? 
              `${summaryData.metadata.start_date} - ${summaryData.metadata.end_date}` : 
              null
            }
          />

          {/* Charts Grid */}
          {/* Top Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
            <Col xs={24} sm={12} md={6}>
              {renderChartCard(
                'Cost Bar Chart',
                chart4Data && !chart4Data.error ? (
                  <BarChartComponent 
                    data={chart4Data}
                    title="Cost Bar Chart"
                    stacked={true}
                  />
                ) : null,
                loadingStates.chart4,
                chart4Data?.error
              )}
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              {renderChartCard(
                'Cost Distribution',
                chart5Data && !chart5Data.error ? (
                  <PieChartComponent 
                    data={chart5Data}
                    title="Cost Distribution by Agent"
                    showLabels={false}
                  />
                ) : null,
                loadingStates.chart5,
                chart5Data?.error
              )}
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              {renderChartCard(
                'Cost Per Trace',
                chart6Data && !chart6Data.error ? (
                  <BarChartComponent 
                    data={chart6Data}
                    title="Cost Per Trace"
                    stacked={true}
                  />
                ) : null,
                loadingStates.chart6,
                chart6Data?.error
              )}
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              <LatestFeedbackCard 
                filters={{
                  timePeriod,
                  //agentNames: agentNames  // Pass actual array, backend will handle empty array as "all"
                  agentNames: []
                }}
              />
            </Col>
          </Row>
          
          {/* Bottom Row */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              {renderChartCard(
                'Tokens In/Out',
                chart1Data && !chart1Data.error ? (
                  <BarChartComponent 
                    data={chart1Data}
                    title="Tokens In/Out"
                    stacked={false}
                  />
                ) : null,
                loadingStates.chart1,
                chart1Data?.error
              )}
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              {renderChartCard(
                'Trace & LLM Call Count',
                chart2Data && !chart2Data.error ? (
                  <BarChartComponent 
                    data={chart2Data}
                    title="Trace Count / LLM Call Count"
                    stacked={false}
                  />
                ) : null,
                loadingStates.chart2,
                chart2Data?.error
              )}
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              {renderChartCard(
                'Trace & LLM Latency',
                chart3Data && !chart3Data.error ? (
                  <BarChartComponent 
                    data={chart3Data}
                    title="Trace / LLM Latency"
                    stacked={false}
                  />
                ) : null,
                loadingStates.chart3,
                chart3Data?.error
              )}
            </Col>
            
            <Col xs={24} sm={12} md={6}>
              {renderChartCard(
                'Tokens Per Trace',
                chart7Data && !chart7Data.error ? (
                  <BarChartComponent 
                    data={chart7Data}
                    title="Token / Trace"
                    stacked={true}
                  />
                ) : null,
                loadingStates.chart7,
                chart7Data?.error
              )}
            </Col>
          </Row>

        </Content>
      </Layout>
      
      {/* Alert Modals */}
      <AlertRulesModal
        visible={alertRulesModalVisible}
        onClose={() => setAlertRulesModalVisible(false)}
      />
      
      <CurrentAlertsCard
        visible={currentAlertsModalVisible}
        onClose={() => setCurrentAlertsModalVisible(false)}
        onAlertsChanged={handleAlertsChanged}
      />
    </Layout>
  );
};

export default AgentObservatoryHome;